import { createRouter, createWebHistory } from 'vue-router'
import { useUserStore } from '../stores/user'
import AdminLoginView from '../views/AdminLoginView.vue'
import CategoryView from '../views/CategoryView.vue'
import { ensureProductImageToken } from '@/utils/productImageToken'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      redirect: '/login'
    },
    {
      path: '/login',
      name: 'login',
      component: AdminLoginView,
      meta: { title: '管理員登入' }
    },
    {
      path: '/dashboard',
      name: 'dashboard',
      component: () => import('../views/DashboardView.vue'),
      meta: { requiresAuth: true, title: '儀表板' }
    },
    {
      path: '/sales-statistics',
      name: 'salesStatistics',
      component: () => import('../views/SalesStatisticsView.vue'),
      meta: { requiresAuth: true, title: '銷售統計' }
    },
    {
      path: '/weekly-sales-statistics',
      name: 'weeklySalesStatistics',
      component: () => import('../views/WeeklySalesStatisticsView.vue'),
      meta: { requiresAuth: true, title: '週平均銷售統計' }
    },
    {
      path: '/users',
      name: 'users',
      component: () => import('../views/UsersView.vue'),
      meta: { requiresAuth: true, title: '用戶管理' }
    },
    {
      path: '/website-settings',
      name: 'websiteSettings',
      component: () => import('../views/WebsiteSettingsView.vue'),
      meta: { requiresAuth: true, title: '頁面設定' }
    },
    {
      path: '/categories',
      name: 'categories',
      component: CategoryView,
      meta: { requiresAuth: true, title: '商品分類' }
    },

    {
      path: '/products',
      name: 'products',
      component: () => import('../views/ProductListView.vue'),
      meta: { requiresAuth: true, title: '商品列表' }
    },
    {
      path: '/products/create',
      name: 'productCreate',
      component: () => import('../views/ProductCreateView.vue'),
      meta: { requiresAuth: true, title: '新增商品' }
    },
    {
      path: '/products/edit/:id',
      name: 'productEdit',
      component: () => import('../views/ProductEditView.vue'),
      meta: { requiresAuth: true, title: '編輯商品' }
    },
    {
      path: '/inventory-count',
      name: 'inventoryCount',
      component: () => import('../views/InventoryCountView.vue'),
      meta: { requiresAuth: true, title: '庫存盤點' }
    },

    {
      path: '/purchase',
      name: 'purchase',
      component: () => import('../views/PurchaseView.vue'),
      meta: { requiresAuth: true, title: '進貨' }
    },
    {
      path: '/purchase/history',
      name: 'purchaseHistory',
      component: () => import('../views/PurchaseHistoryView.vue'),
      meta: { requiresAuth: true, title: '進貨紀錄' }
    },
    {
      path: '/purchase-order-history',
      name: 'purchaseOrderHistory',
      component: () => import('../views/PurchaseOrderHistoryView.vue'),
      meta: { requiresAuth: true, title: '進貨單紀錄' }
    },
    {
      path: '/purchase-order-history/:id',
      name: 'purchaseOrderDetail',
      component: () => import('../views/PurchaseOrderHistoryView.vue'),
      meta: { requiresAuth: true, title: '進貨單詳情' }
    },
    {
      path: '/supplier',
      name: 'supplier',
      component: () => import('../views/SupplierView.vue'),
      meta: { requiresAuth: true, title: '廠商管理' }
    },
    {
      path: '/orders',
      name: 'orders',
      component: () => import('../views/Orders.vue'),
      meta: { requiresAuth: true, title: '訂單管理' }
    },
    {
      path: '/order-verification',
      name: 'orderVerification',
      component: () => import('../views/OrderVerificationView.vue'),
      meta: { requiresAuth: true, title: '對單系統' }
    },
    {
      path: '/return-requests',
      name: 'returnRequests',
      component: () => import('../views/ReturnRequestsView.vue'),
      meta: { requiresAuth: true, title: '退貨總覽' }
    },
    {
      path: '/product-requests',
      name: 'productRequests',
      component: () => import('../views/ProductRequestsView.vue'),
      meta: { requiresAuth: true, title: '產品代尋留言板' }
    },

    {
      path: '/cost-calculator',
      name: 'costCalculator',
      component: () => import('../views/CostCalculatorView.vue'),
      meta: { requiresAuth: true, title: '算錢工具' }
    },


    {
      path: '/manual-invoice',
      name: 'manualInvoice',
      component: () => import('../views/ManualInvoiceView.vue'),
      meta: { requiresAuth: true, title: '手動開發票' }
    },
    {
      path: '/invoice-management',
      name: 'invoiceManagement',
      component: () => import('../views/InvoiceManagementView.vue'),
      meta: { requiresAuth: true, title: '發票管理' }
    },

    {
      path: '/daily-delivery',
      name: 'dailyDelivery',
      component: () => import('../views/DailyDeliveryView.vue'),
      meta: { requiresAuth: true, title: '當日配送', mobileOnly: true }
    },
    {
      path: '/delivery-tracking',
      name: 'deliveryTracking',
      component: () => import('../views/DeliveryTrackingView.vue'),
      meta: { requiresAuth: true, title: '配送員追蹤' }
    }
  ]
})

// 添加導航錯誤處理
router.onError((error) => {
  console.error('路由導航錯誤:', error);
});

router.beforeEach(async (to, from, next) => {
  // 特別處理商品分類路由
  if (to.path === '/categories') {
    console.log('正在載入商品分類頁面...');
  }

  const userStore = useUserStore()

  // 登入頁無需認證，允許多帳號同時登入
  if (to.path === '/login') {
    // 不再自動重定向已登入用戶，允許在登入頁面切換帳號
    next() // 允許訪問登入頁
    return
  }

  // 檢查是否需要認證
  if (to.matched.some(record => record.meta.requiresAuth)) {
    // 分頁沒有 token（關掉瀏覽器後重開就是這種情況），先用「記住我」的續期票換發
    if (!userStore.isLoggedIn && userStore.hasSavedRefreshSession()) {
      await userStore.attemptTokenRefresh()
    }

    if (!userStore.isLoggedIn) {
      // 未登入管理員轉到登入頁
      next('/login')
    } else {
      // 驗證token有效性，如果無效會嘗試自動重新登入
      const isValid = await userStore.validateToken()
      if (!isValid) {
        // 自動重新登入失敗，轉到登入頁
        next('/login')
        return
      }

      // 商品圖片需要通行證，進頁面前先備妥，避免第一次顯示時破圖
      await ensureProductImageToken()

      // 檢查是否為chengshe帳號，如果是則只能訪問當日配送頁面
      if (userStore.user?.username === 'chengshe') {
        if (to.path !== '/daily-delivery') {
          next('/daily-delivery')
          return
        }
      }

      // 檢查是否需要admin權限
      if (to.matched.some(record => record.meta.requiresAdmin)) {
        if (userStore.user?.role !== 'admin') {
          // 非admin用戶，轉到儀表板
          next('/dashboard')
          return
        }
      }

      // 檢查是否為當日配送專用頁面
      if (to.matched.some(record => record.meta.mobileOnly)) {
        if (userStore.user?.username !== 'chengshe') {
          // 非chengshe用戶不能訪問當日配送專用頁面
          next('/dashboard')
          return
        }
      }

      next() // 允許訪問
    }
  } else {
    next() // 無需認證的頁面
  }
})

router.beforeResolve((to, from, next) => {
  if (to.name === 'categories') {
    console.log('正在解析商品分類路由，確保組件已載入');
  }
  next();
});

export default router